import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DescriptionItem,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  StatusPill,
  TableWrap,
  Td,
  Textarea,
  Th,
  Tr
} from '../../components/admin/ui';
import ChangePasswordForm from '../../components/ChangePasswordForm';
import { IconAlert, IconCard } from '../../components/icons';
import { fieldErrorsFrom } from '../../utils/formErrors';
import {
  formatDateTime,
  transactionTypeLabels,
  transactionTypeTone
} from '../../components/admin/labels';
import {
  billingLabel,
  merchantRoleLabel,
  pilotPreviouslyUsed,
  planLabel,
  userStatusTone
} from '../../components/plans';
import { useMerchantSession } from '../../components/merchant/MerchantLayout';
import { PlanUpgradeGrid } from '../../components/merchant/PlanUpgradeGrid';
import { useToast } from '../../components/admin/overlays';

const emptyCancelForm = { timing: 'end_of_period', note: '' };

const MerchantAccount = () => {
  const session = useMerchantSession();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelForm, setCancelForm] = useState(emptyCancelForm);
  const [cancelErrors, setCancelErrors] = useState({});
  const [requesting, setRequesting] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const response = await api.get('/api/portal/me');
      setData(response.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    api
      .get('/api/portal/transactions')
      .then(({ data: txData }) => setTransactions(txData.transactions || []))
      .catch(() => setTransactions([]))
      .finally(() => setTxLoading(false));
  }, []);

  const handleOpenBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data: portal } = await api.post('/api/portal/billing-portal');
      if (portal?.url) {
        window.location.href = portal.url;
        return;
      }
      toast('Could not open the billing portal.', { tone: 'error' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not open the billing portal.', { tone: 'error' });
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleRequestCancellation = async () => {
    if (!cancelForm.note.trim()) {
      setCancelErrors({ note: 'Tell us why you are cancelling.' });
      return;
    }
    setCancelErrors({});

    setRequesting(true);
    try {
      await api.post('/api/portal/cancellation-request', {
        timing: cancelForm.timing,
        note: cancelForm.note.trim()
      });
      setCancelOpen(false);
      setCancelForm(emptyCancelForm);
      await fetchMe();
      toast('Cancellation request sent — our team will be in touch.', { tone: 'success' });
    } catch (error) {
      setCancelErrors(
        fieldErrorsFrom(error, { fallback: 'Could not send this request. Please try again.' })
      );
    } finally {
      setRequesting(false);
    }
  };

  // Stripe redirects here the instant Checkout completes — which can easily
  // be before the `checkout.session.completed` webhook has actually applied
  // the new plan (or, in a local dev environment with no `stripe listen`
  // forwarding running, before it ever does), leaving this page re-fetching
  // merchant data that still shows the old plan and the upgrade prompt
  // reappearing as if nothing had happened. Confirming the session directly
  // removes that dependency on webhook timing.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment !== 'success' && payment !== 'cancelled') return;

    const sessionId = params.get('session_id');
    // Strip only the payment params — ?tab= has to survive, or confirming a
    // payment would bounce the merchant off the tab Stripe sent them back to.
    params.delete('payment');
    params.delete('session_id');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));

    if (payment === 'cancelled') {
      toast('Payment was cancelled.', { tone: 'info' });
      return;
    }
    if (!sessionId) return;

    api
      .post('/api/stripe/confirm-plan-checkout', { sessionId })
      .then(({ data: confirmData }) => {
        toast(
          confirmData?.activated ? 'Plan updated.' : 'Payment received.',
          { tone: 'success' }
        );
      })
      .catch((err) => {
        console.error('Confirm checkout error:', err);
        toast('Payment received, but we could not confirm it just now — refresh in a moment.', {
          tone: 'error'
        });
      })
      .finally(fetchMe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = data?.user || session;
  const merchant = data?.merchant;
  // Billing is admin-only, matching how MerchantTeam gates seat management —
  // an agent can work cases but should not be the one changing what the
  // business pays for.
  const isAdmin = (user?.merchantRole || session?.merchantRole) === 'admin';
  // billingModel stays 'subscription' after a cancellation (it records what
  // they were on), so it alone would keep showing the "here's your plan"
  // summary to someone who no longer has one — and no way to buy again.
  // Stripe's own status is what says whether it is still live.
  const subscriptionEnded = merchant?.subscription?.status === 'canceled';
  const subscribed = merchant?.billingModel === 'subscription' && !subscriptionEnded;
  const seatPrice = Number(merchant?.seatPrice) || 0;
  // The owner's own seat is included with the plan, so there is always at
  // least one — and it is seats, not users, that the bill is counted in.
  const seatsAllowed = Math.max(1, merchant?.seats?.allowed || 0);
  const seatsUsed = merchant?.seats?.used || 0;
  const cancellation = merchant?.cancellationRequest || null;
  const cancellationPending = cancellation?.status === 'pending';

  /* ── Tabs ───────────────────────────────────────────────────────────────── */

  // Billing only exists for an admin, so an agent gets a single tab and no
  // tab bar at all rather than two they can never open.
  const tabs = [
    { id: 'details', label: 'Details' },
    ...(isAdmin
      ? [
          { id: 'subscription', label: 'Subscription' },
          { id: 'billing', label: 'Billing history' }
        ]
      : [])
  ];

  // Held in the URL rather than component state so a reload — including the
  // one Stripe sends them back through — lands on the tab they were on.
  const requestedTab = searchParams.get('tab');
  const tab = tabs.some((t) => t.id === requestedTab) ? requestedTab : 'details';

  const selectTab = (id) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', id);
        return next;
      },
      // Replace rather than push: Back should leave the account page, not
      // walk back through every tab the user happened to click.
      { replace: true }
    );
  };

  const onTabKeyDown = (e, index) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = tabs[(index + dir + tabs.length) % tabs.length];
    selectTab(next.id);
    document.getElementById(`account-tab-${next.id}`)?.focus();
  };

  return (
    <div id="upgrade" className="space-y-6">
      {tabs.length > 1 && (
        <div className="border-b border-ink-200">
          <div role="tablist" aria-label="Account sections" className="-mb-px flex flex-wrap gap-1">
            {tabs.map((t, index) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  id={`account-tab-${t.id}`}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  aria-controls={`account-panel-${t.id}`}
                  tabIndex={active ? 0 : -1}
                  onClick={() => selectTab(t.id)}
                  onKeyDown={(e) => onTabKeyDown(e, index)}
                  className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                    active
                      ? 'border-brand-800 font-600 text-brand-800'
                      : 'border-transparent font-medium text-ink-500 hover:border-ink-300 hover:text-ink-900'
                  }`}
                >
                  {t.label}
                  {t.id === 'subscription' && cancellationPending && (
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-warning-600"
                      title="Cancellation requested"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'details' && (
      <div id="account-panel-details" role="tabpanel" aria-labelledby="account-tab-details" className="space-y-6">
      <Card>
        <CardHeader title="Your details" description="Who you are signed in as." />
        <CardBody>
          {loading ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <DescriptionItem label="Name">{user?.name || '—'}</DescriptionItem>
              <DescriptionItem label="Email">{user?.email || '—'}</DescriptionItem>
              <DescriptionItem label="Role">
                {merchantRoleLabel(user?.merchantRole)}
              </DescriptionItem>
              <DescriptionItem label="Status">
                <StatusPill tone={userStatusTone(user?.status)}>{user?.status || 'active'}</StatusPill>
              </DescriptionItem>
              <DescriptionItem label="Last sign-in">
                {user?.lastLogin ? formatDateTime(user.lastLogin) : 'This is your first session'}
              </DescriptionItem>
              {merchant && (
                <>
                  <DescriptionItem label="Account">{merchant.name}</DescriptionItem>
                  <DescriptionItem label="Billing">
                    {billingLabel(merchant)}
                    {pilotPreviouslyUsed(merchant) && (
                      <span className="ml-1.5 text-xs text-ink-500">(pilot previously used)</span>
                    )}
                  </DescriptionItem>
                </>
              )}
            </dl>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Password"
          description="Choose something you don't use anywhere else."
        />
        <CardBody>
          <div className="max-w-2xl">
            <ChangePasswordForm />
          </div>
        </CardBody>
      </Card>
      </div>
      )}

      {tab === 'subscription' && (
      <div id="account-panel-subscription" role="tabpanel" aria-labelledby="account-tab-subscription" className="space-y-6">
      {/* There is one paid plan, sold per user, so for a merchant already on
          it "change your plan" has nothing to change to — what actually
          varies is how many people are on the account. They get the bill;
          everyone else (pilot, pay-per-case, nothing yet) gets the chooser.
          The chooser is also reachable as a modal from the dashboard, cases
          and team pages; this is the one place it sits inline. */}
      {loading ? (
        <Card>
          <CardBody>
            <Skeleton className="h-24 w-full" />
          </CardBody>
        </Card>
      ) : (
      merchant && (
        subscribed ? (
          <Card>
            <CardHeader
              title="Subscription"
              description="ParcelBuddy is priced per user — your bill follows the size of your team."
              actions={
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={openingPortal}
                    disabled={openingPortal}
                    onClick={handleOpenBillingPortal}
                  >
                    <IconCard size={15} />
                    Update payment method
                  </Button>
                  {!cancellationPending && (
                    <Button variant="dangerGhost" size="sm" onClick={() => setCancelOpen(true)}>
                      Request cancellation
                    </Button>
                  )}
                </>
              }
            />
            <CardBody>
              {/* A cancellation is handled by a person, not a switch, so the
                  merchant needs to see their request is actually in hand. */}
              {cancellationPending && (
                <div className="mb-5 flex items-start gap-3 rounded-xl bg-warning-50 px-4 py-3.5 ring-1 ring-inset ring-warning-100">
                  <IconAlert size={17} className="mt-0.5 shrink-0 text-warning-600" />
                  <div className="min-w-0 text-sm text-warning-700">
                    <p className="font-600">Cancellation requested</p>
                    <p className="mt-0.5">
                      You asked to cancel{' '}
                      {cancellation.timing === 'immediate'
                        ? 'as soon as possible'
                        : 'at the end of your current billing period'}
                      {cancellation.requestedAt && ` on ${formatDateTime(cancellation.requestedAt)}`}. Our
                      team will confirm by email — your subscription stays active until then.
                    </p>
                  </div>
                </div>
              )}

              {merchant.subscription?.cancelAt && (
                <div className="mb-5 flex items-start gap-3 rounded-xl bg-danger-50 px-4 py-3.5 ring-1 ring-inset ring-danger-100">
                  <IconAlert size={17} className="mt-0.5 shrink-0 text-danger-600" />
                  <p className="text-sm text-danger-700">
                    <span className="font-600">Scheduled to end</span> on{' '}
                    {formatDateTime(merchant.subscription.cancelAt)}. You keep full access until then.
                  </p>
                </div>
              )}

              <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <DescriptionItem label="Plan">
                  {planLabel(merchant.plan)}
                  {seatPrice > 0 && (
                    <span className="ml-1.5 text-xs text-ink-500">
                      £{seatPrice.toFixed(2)} per user / month
                    </span>
                  )}
                </DescriptionItem>
                <DescriptionItem label="Users">
                  {seatsUsed} of {seatsAllowed} seat{seatsAllowed === 1 ? '' : 's'}
                </DescriptionItem>
                <DescriptionItem label="Monthly total">
                  {seatPrice > 0 ? `£${(seatsAllowed * seatPrice).toFixed(2)}` : '—'}
                </DescriptionItem>
                <DescriptionItem label="Status">
                  <StatusPill tone={merchant.subscription?.status === 'active' ? 'success' : 'warning'}>
                    {merchant.subscription?.status || 'active'}
                  </StatusPill>
                </DescriptionItem>
                <DescriptionItem label="Renews">
                  {merchant.subscription?.currentPeriodEnd
                    ? formatDateTime(merchant.subscription.currentPeriodEnd)
                    : '—'}
                </DescriptionItem>
                <DescriptionItem label="Cases">Unlimited</DescriptionItem>
              </dl>

              <p className="mt-5 border-t border-ink-100 pt-4 text-sm text-ink-600">
                Seats follow your team: adding someone on{' '}
                <Link
                  to="/merchant/team"
                  className="font-600 text-brand-700 underline hover:text-brand-800"
                >
                  Team
                </Link>{' '}
                adds a paid seat, and removing them releases it again.
              </p>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={subscriptionEnded ? 'Start a new subscription' : 'Choose a plan'}
              description={
                subscriptionEnded
                  ? 'Your previous subscription has ended. Subscribing again restores full access straight away.'
                  : "Unlimited cases, priced per user — move onto a plan whenever you're ready."
              }
            />
            <CardBody>
              {subscriptionEnded && (
                <div className="mb-5 flex items-start gap-3 rounded-xl bg-warning-50 px-4 py-3.5 ring-1 ring-inset ring-warning-100">
                  <IconAlert size={17} className="mt-0.5 shrink-0 text-warning-600" />
                  <p className="text-sm text-warning-700">
                    Until you subscribe again you can still see your cases, but you can&apos;t take
                    action on them or raise new ones.
                  </p>
                </div>
              )}
              <PlanUpgradeGrid merchant={merchant} />
            </CardBody>
          </Card>
        )
      )
      )}
      </div>
      )}

      {tab === 'billing' && (
      <div id="account-panel-billing" role="tabpanel" aria-labelledby="account-tab-billing" className="space-y-6">
        {/* Every payment ParcelBuddy has taken, from the same ledger the admin
            console reads — so the merchant can reconcile their own bill
            without having to ask. */}
        <Card>
          <CardHeader title="Billing history" description="Payments we've taken on this account." />
          {txLoading ? (
            <CardBody>
              <Skeleton className="h-20 w-full" />
            </CardBody>
          ) : transactions.length === 0 ? (
            <EmptyState
              icon={IconCard}
              title="No payments yet"
              description="Charges appear here as soon as they're taken."
            />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Description</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Amount</Th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <Tr key={tx._id}>
                    <Td className="whitespace-nowrap text-ink-600">{formatDateTime(tx.occurredAt)}</Td>
                    <Td className="text-ink-900">{tx.description || '—'}</Td>
                    <Td>
                      <StatusPill tone={transactionTypeTone[tx.type] || 'info'}>
                        {transactionTypeLabels[tx.type] || tx.type}
                      </StatusPill>
                    </Td>
                    <Td className="text-right font-600 text-ink-900">
                      £{Number(tx.amount || 0).toFixed(2)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      </div>
      )}

      {/* A request, not a switch: an admin carries out the cancellation, so
          this collects what they need to act on it and why. */}
      <Modal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelErrors({});
        }}
        title="Request cancellation"
        description="We'll pass this to our team, who will confirm by email. Nothing is cancelled until they do."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              Keep my subscription
            </Button>
            <Button variant="danger" onClick={handleRequestCancellation} loading={requesting}>
              Send request
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {cancelErrors._general && (
            <div
              role="alert"
              className="rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
            >
              {cancelErrors._general}
            </div>
          )}

          <Field label="When should it take effect?" htmlFor="cancel-timing" required>
            <Select
              id="cancel-timing"
              value={cancelForm.timing}
              onChange={(e) => setCancelForm({ ...cancelForm, timing: e.target.value })}
            >
              <option value="end_of_period">
                End of my billing period
                {merchant?.subscription?.currentPeriodEnd
                  ? ` (${formatDateTime(merchant.subscription.currentPeriodEnd)})`
                  : ''}
              </option>
              <option value="immediate">As soon as possible</option>
            </Select>
          </Field>

          <Field
            label="Why are you cancelling?"
            htmlFor="cancel-note"
            required
            error={cancelErrors.note}
            hint="This goes straight to our team — tell us what went wrong or what you need."
          >
            <Textarea
              id="cancel-note"
              rows={4}
              maxLength={2000}
              className="resize-none"
              value={cancelForm.note}
              onChange={(e) => setCancelForm({ ...cancelForm, note: e.target.value })}
              placeholder="Too expensive, not enough cases, missing a feature…"
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
};

export default MerchantAccount;
